import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tableColumnsV2 } from '../../tableColumns/membershipColumns';
import Card from '@mui/material/Card';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { history } from '../../utils';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import useUrlState from '../../hooks/use-url-state';
import { ISortConfig } from '../../ts/interface/components/redesing/table.interface';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';
import { useUserMembershipsRelayQuery } from '../../hooks/membership/query/V2/useUserMembershipsRelay';
import { mapUserMembershipsToLegacy } from '../../hooks/membership/query/V2/membershipMappers';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';

/**
 * Scope for the pending-memberships query: either a system (system view) or an
 * organization (org view). Exactly one is provided by the parent.
 */
type PendingMembershipsScope = { systemId: string } | { organizationId: string };

interface IPendingMembershipsProps {
  scope: PendingMembershipsScope;
}

const PendingMemberships: React.FC<IPendingMembershipsProps> = ({ scope }) => {
  // Server-side keyset pagination (page# kept local; the hook owns it).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const [pageState, setPageState] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    sortConfig: {} as ISortConfig,
  });

  const debouncedSearch = useSearchDebounce(pageState.search);

  const sorting: ISortConfig = useMemo(() => {
    return {
      key: pageState.sortConfig.key || 'created_on',
      direction: pageState.sortConfig.direction || 'asc',
    };
  }, [pageState.sortConfig.key, pageState.sortConfig.direction]);

  // Map the table sort key to the GraphQL UserMembershipSortInput. Only the
  // pending columns' sortable keys are represented; anything else falls back to
  // CREATED_ON (the request date).
  const sortConfig = useMemo(() => {
    const keyMap: Record<string, 'CREATED_ON' | 'EMAIL'> = {
      created_on: 'CREATED_ON',
      userName: 'EMAIL',
    };
    const field = keyMap[sorting.key] || 'CREATED_ON';
    const order = sorting.direction?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
    return [{ field, order: order as 'ASC' | 'DESC' }];
  }, [sorting.key, sorting.direction]);

  const {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isRefetching,
  } = useUserMembershipsRelayQuery(
    {
      ...scope,
      pending: true,
    },
    {
      after,
      limit: pageState.sizePerPage,
      sort: sortConfig,
      search: debouncedSearch || undefined,
    }
  );

  // Render ONLY the current page (no client-side accumulation).
  const pendingUserMemberships = useMemo(
    () => (data ? mapUserMembershipsToLegacy(data) : []),
    [data]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [exportData, setExportData] = useState<boolean>(false);
  const columns = useMemo(() => {
    return tableColumnsV2('pendingMemberships');
  }, []);

  const handleSortChange = useCallback(
    (sort: ISortConfig) => {
      resetPagination();
      setPageState((prevState) => ({
        ...prevState,
        sortConfig: sort,
      }));
    },
    [resetPagination, setPageState]
  );

  const handleSearch = useCallback(
    (search: React.ChangeEvent<HTMLInputElement>) => {
      resetPagination();
      setPageState((prevState) => ({ ...prevState, search: search.target.value }));
    },
    [resetPagination, setPageState]
  );

  const handleRowsPerPageChange = useCallback(
    (rowsPerPage: React.ChangeEvent<HTMLInputElement>) => {
      resetPagination();
      setPageState((prevState) => ({
        ...prevState,
        sizePerPage: Number(rowsPerPage.target.value),
      }));
    },
    [resetPagination, setPageState]
  );

  return (
    <Card>
      <PaginationTable
        isLoading={isLoading || isPaginating}
        isLoadingCache={isRefetching}
        padding={{
          pr: 1,
          pt: 1,
        }}
        showFilter={false}
        exportData={exportData}
        csvFileName={'Pending_memberships.csv'}
        onExport={() => setExportData(false)}
        buttons={[
          {
            label: 'Export',
            onClick: () => setExportData(true),
            startIcon: <FileDownloadIcon />,
          },
        ]}
        sortBy={sorting.key}
        onSortChange={(sort) => handleSortChange(sort as ISortConfig)}
        items={pendingUserMemberships || []}
        totalItems={count}
        hasMore={hasMore}
        noDataSlice
        localSearch={false}
        noDataText={'There are no pending user memberships at this time'}
        onRowCLick={(id: string) => history.push(`/memberships/user/${id}`)}
        columns={columns}
        rowsPerPage={pageState.sizePerPage}
        page={pageNumber}
        onPageChange={(_page, pageN: number) => onPageChange(pageN)}
        onRowsPerPageChange={handleRowsPerPageChange}
        searchText={pageState.search}
        searchPlaceholder={'Search pending memberships'}
        handleSearch={handleSearch}
      />
    </Card>
  );
};

export default PendingMemberships;
